import { answeredFields, readAnswers, type Answers } from "./answers";
import type { Selection } from "./articles";
import { standOf, type Recorded } from "./participant";

/**
 * ═══ UNE CORRECTION EN ATTENTE, ET CE QUI LA PERD (bug #258) ═══
 *
 * Quand on revient corriger une inscription enregistrée, ce qu'on change vit dans l'état
 * de navigation jusqu'à « Enregistrer » ou jusqu'à la confirmation (voir
 * `funnel/stepSave.tsx`). Quitter le parcours autrement le jetait sans un mot : un jour
 * coché, « Accueil » dans l'en-tête, et l'inscription relue ne portait que l'ancien jour.
 * Ces deux règles décident du garde de `funnel/leaveGuard.ts` ; elles sont ici, sans
 * React, pour se tester seules.
 *
 * Les textes affichés sont français : c'est un produit français.
 */

/** Ce que le serveur détient, dans les champs qu'une étape peut corriger. */
type Held = Pick<Recorded, "answer" | "reason" | "typoId" | "days" | "extended">;

/**
 * L'ÉTAT DE NAVIGATION PORTE-T-IL AUTRE CHOSE QUE CE QUI EST ENREGISTRÉ ?
 *
 * ⚠️ COMPARÉ À L'INSCRIPTION, PAS À LA SIMPLE PRÉSENCE D'UNE RÉPONSE. « Continuer » emporte
 * les réponses de chaque étape même inchangées : la participation, le remplaçant, la
 * sélection de billetterie. Compter leur seule présence aurait averti quiconque a
 * simplement relu son inscription, et un avertissement qui crie à chaque fois ne se lit
 * plus.
 *
 * ⚠️ LE MOTIF ET LE REMPLAÇANT NE COMPTENT QUE POUR LA RÉPONSE QUI LES LIT. L'étape
 * participation les emporte toujours, vides ou non ; l'écriture ne les pose que pour
 * « ne vient pas » et « représenté » (voir `funnel/participant.ts`).
 *
 * ⚠️ UNE SÉLECTION DE BILLETTERIE DONT ON NE CONNAÎT PAS LA COMMANDE COMPTE COMME
 * CHANGÉE. Mieux vaut un avertissement de trop qu'une commande perdue sans un mot.
 */
export function unsavedAnswers(
  answers: Answers,
  {
    recorded,
    order,
  }: {
    recorded: Held;
    /** La commande enregistrée, quand on la connaît. */
    order: Selection | undefined;
  },
): boolean {
  const answer = answers.participation ?? recorded.answer;

  if (
    answers.participation !== undefined &&
    answers.participation !== recorded.answer
  ) {
    return true;
  }
  if (
    answer === "decline" &&
    answers.reason !== undefined &&
    answers.reason.trim() !== (recorded.reason ?? "").trim()
  ) {
    return true;
  }
  if (
    answer === "represented" &&
    answers.stand !== undefined &&
    !sameStand(answers.stand, standOf(recorded.extended))
  ) {
    return true;
  }
  if (answers.typoId !== undefined && answers.typoId !== recorded.typoId) {
    return true;
  }
  if (answers.days !== undefined && !sameIds(answers.days, recorded.days ?? [])) {
    return true;
  }
  if (
    answers.articles !== undefined &&
    !(order && sameOrder(answers.articles, order))
  ) {
    return true;
  }
  return false;
}

/**
 * CETTE NAVIGATION LAISSE-T-ELLE LES RÉPONSES DERRIÈRE ELLE ?
 *
 * Elle les garde quand elle les emporte TOUTES vers un écran qui s'en sert : le même
 * écran (`record` remplace l'entrée d'historique à chaque clic), ou une étape du parcours
 * — « Continuer », « Retour », le fil d'Ariane, et la confirmation, qui écrit ce qu'elle
 * reçoit.
 *
 * ⚠️ EMPORTER NE SUFFIT PAS HORS DU PARCOURS. « Retour à l'accueil » des pages de
 * correction emporte les réponses lui aussi (`useFunnelNav.go`), mais l'accueil n'en fait
 * rien : elles y sont perdues comme si on ne les avait pas emportées.
 */
export function dropsAnswers({
  answers,
  from,
  to,
  funnel,
}: {
  answers: Answers;
  /** Le chemin qu'on quitte. */
  from: string;
  /** Où l'on va, et ce qu'on y emporte. */
  to: { pathname: string; state: unknown };
  /** Le chemin du parcours, contexte de l'événement compris. */
  funnel: string;
}): boolean {
  const inFunnel =
    to.pathname === funnel || to.pathname.startsWith(`${funnel}/`);
  if (to.pathname !== from && !inFunnel) return true;
  const carried = readAnswers(to.state);
  return answeredFields(answers).some((field) => carried[field] === undefined);
}

function sameIds(a: ReadonlyArray<number>, b: ReadonlyArray<number>): boolean {
  const norm = (ids: ReadonlyArray<number>) =>
    [...new Set(ids)].sort((x, y) => x - y).join();
  return norm(a) === norm(b);
}

/* Les quantités se répètent dans une liste : triées, pas dédupliquées. Un jour sans
   article ne se distingue pas d'un jour absent. */
function sameOrder(a: Selection, b: Selection): boolean {
  const norm = (s: Selection) =>
    JSON.stringify(
      Object.entries(s)
        .filter(([, ids]) => ids.length > 0)
        .map(([day, ids]) => [day, [...ids].sort((x, y) => x - y)])
        .sort(([x], [y]) => String(x).localeCompare(String(y))),
    );
  return norm(a) === norm(b);
}

function sameStand(
  typed: NonNullable<Answers["stand"]>,
  held: NonNullable<Answers["stand"]> | undefined,
): boolean {
  const norm = (s: NonNullable<Answers["stand"]> | undefined) =>
    [s?.first, s?.last, s?.email].map((v) => (v ?? "").trim()).join("\n");
  return norm(typed) === norm(held);
}
